/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Search, MessageCircle, Building2, Briefcase, MapPin, Users, ShieldCheck, Sparkles } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { LeadershipBadge, LeadershipTitle } from '../../components/LeadershipBadge';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const avatarColors = [
  'from-[#E63946] to-[#C62D3A]', 'from-[#457B9D] to-[#1D3557]',
  'from-[#F4A261] to-[#E76F51]', 'from-[#2A9D8F] to-[#264653]',
  'from-[#6C5CE7] to-[#A29BFE]', 'from-[#00B894] to-[#00CEC9]',
];
const getAvatarColor = (id) => avatarColors[(id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % avatarColors.length];

const industryLabels = {
  restaurants: 'Restaurants & Hospitality', professional: 'Professional Services', health: 'Health & Wellness',
  real_estate: 'Real Estate', finance: 'Finance', marketing: 'Marketing & Media', technology: 'Technology',
  education: 'Education', retail: 'Retail', community: 'Community Organizations', other: 'Other'
};

// Role-based theming: Official (gold) / Board (red) / Regular (blue)
const getRoleTheme = (member) => {
  const level = member?.leadership_level;
  if (level === 1) {
    return {
      kind: 'official',
      label: 'Official',
      bannerStyle: {
        backgroundImage:
          'linear-gradient(135deg, #1a1404 0%, #3a2710 40%, #8a6a1a 100%)',
      },
      bannerOverlay:
        'repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(218,165,32,0.14) 8px, rgba(218,165,32,0.14) 16px)',
      ring: 'ring-1 ring-amber-300/70',
      avatarBorder: 'border-amber-200',
      chipClass: 'bg-amber-100 text-amber-800 border-amber-200',
      chipIcon: ShieldCheck,
      hoverBorder: 'hover:border-amber-300',
      titleColor: 'text-[#8a6a1a]',
    };
  }
  if (level === 2 || level === 3) {
    return {
      kind: 'board',
      label: level === 3 ? 'Committee' : 'Board',
      bannerStyle: {
        backgroundImage:
          'linear-gradient(135deg, #4a0d13 0%, #8c1a24 50%, #E63946 100%)',
      },
      bannerOverlay:
        'repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(255,255,255,0.08) 8px, rgba(255,255,255,0.08) 16px)',
      ring: '',
      avatarBorder: 'border-white',
      chipClass: 'bg-rose-100 text-rose-800 border-rose-200',
      chipIcon: ShieldCheck,
      hoverBorder: 'hover:border-rose-300',
      titleColor: 'text-[#E63946]',
    };
  }
  return {
    kind: 'member',
    label: 'Member',
    // Lighter banner for regular members so business logos remain legible on top
    bannerStyle: {
      backgroundImage: 'linear-gradient(135deg, #EEF2F7 0%, #E2E8F0 50%, #CBD5E1 100%)',
    },
    bannerOverlay:
      'repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(0,34,73,0.04) 8px, rgba(0,34,73,0.04) 16px)',
    ring: '',
    avatarBorder: 'border-white',
    chipClass: 'bg-white text-[#002249] border-[#002249]/20 shadow-sm',
    chipIcon: Sparkles,
    hoverBorder: 'hover:border-[#002249]/20',
    titleColor: 'text-[#002249]',
  };
};

const PortalMembersDirectory = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | official | board | member

  useEffect(() => { fetchMembers(); }, [search]);  

  const fetchMembers = async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await axios.get(`${API}/portal/members${params}`, { withCredentials: true });
      setMembers(res.data);
    } catch { toast.error('Failed to load members'); } finally { setLoading(false); }
  };

  const filteredMembers = members.filter((m) => {
    if (filter === 'all') return true;
    const theme = getRoleTheme(m);
    return theme.kind === filter;
  });

  const counts = members.reduce((acc, m) => {
    const k = getRoleTheme(m).kind;
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  const FILTER_TABS = [
    { id: 'all', label: 'All', count: members.length, dot: '#64748b' },
    { id: 'official', label: 'Official', count: counts.official || 0, dot: '#DAA520' },
    { id: 'board', label: 'Board', count: counts.board || 0, dot: '#E63946' },
    { id: 'member', label: 'Members', count: counts.member || 0, dot: '#002249' },
  ];

  return (
    <div className="min-h-screen bg-[#F0F2F5] py-5 px-4" data-testid="portal-members-directory">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#050505]">Members</h1>
            <p className="text-sm text-[#65676B] mt-0.5">{members.length} members in the DCCF community</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#65676B]" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search members..." className="pl-10 h-10 rounded-xl bg-white border-slate-200 text-[16px]" data-testid="members-search" />
          </div>
        </div>

        {/* Role filter tabs */}
        <div className="flex items-center gap-1.5 mb-5 overflow-x-auto scrollbar-hide pb-1" data-testid="role-filter-tabs">
          {FILTER_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              className={`flex items-center gap-1.5 px-3 h-8 rounded-full text-[12.5px] font-medium whitespace-nowrap transition-all ${
                filter === t.id
                  ? 'bg-[#002249] text-white shadow-sm'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
              data-testid={`role-filter-${t.id}`}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.dot }} />
              {t.label}
              <span className={`text-[10.5px] font-semibold ${filter === t.id ? 'text-white/80' : 'text-slate-400'}`}>
                {t.count}
              </span>
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-[#E63946] border-t-transparent rounded-full" /></div>
        ) : filteredMembers.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMembers.map((member, i) => {
              const cp = member.company_profile;
              const pic = member.picture;
              const displayName = member.display_name || member.name;
              const isMe = member.id === user?.id;
              const theme = getRoleTheme(member);
              const ChipIcon = theme.chipIcon;
              // Detect business card: non-leadership member with a company logo
              const isBusinessCard = !member.leadership_level && cp?.logo_url;
              return (
                <motion.div key={member.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.15) }}>
                  <Link to={`/portal/members/${member.id}`}
                    className={`block bg-white rounded-xl shadow-sm hover:shadow-md transition-all border border-transparent ${theme.hoverBorder} ${theme.ring} group`}
                    data-testid={`member-card-${i}`}
                    data-role={theme.kind}
                    data-business={isBusinessCard ? 'true' : 'false'}
                  >
                    {isBusinessCard ? (
                      /* ── Business card: dedicated white logo panel ── */
                      <div
                        className="h-28 relative rounded-t-xl overflow-hidden bg-white flex items-center justify-center border-b border-slate-100"
                        data-testid={`business-logo-panel-${i}`}
                      >
                        {/* Subtle light pattern */}
                        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(circle at 30% 30%, rgba(0,34,73,0.04) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(230,57,70,0.03) 0%, transparent 50%)' }} />
                        <Img variant="business"
                          src={resolveImg(cp.logo_url)}
                          alt={cp.company_name}
                          className="relative z-10 max-h-20 max-w-[70%] object-contain drop-shadow-sm transition-transform group-hover:scale-105"
                        />
                        {/* Top-right "Business" chip */}
                        <div className="absolute top-2 right-2 z-10">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-white text-[#002249] border-[#002249]/20 shadow-sm">
                            <Building2 className="w-3 h-3" />
                            Business
                          </span>
                        </div>
                      </div>
                    ) : (
                      /* ── Leadership / individual card: role-coded banner ── */
                      <div
                        className="h-16 relative rounded-t-xl overflow-hidden"
                        style={theme.bannerStyle}
                        data-testid={`member-banner-${theme.kind}`}
                      >
                        <div
                          className="absolute inset-0 opacity-60"
                          style={{ backgroundImage: theme.bannerOverlay }}
                        />
                        {/* Top-right role chip */}
                        <div className="absolute top-2 right-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${theme.chipClass}`}
                          >
                            <ChipIcon className="w-3 h-3" />
                            {theme.label}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className={`px-4 pb-4 ${isBusinessCard ? 'pt-4' : '-mt-8'} relative z-10`}>
                      {/* Avatar — only on non-business cards (business shows logo above) */}
                      {!isBusinessCard && (
                        <div className={`w-16 h-16 rounded-full border-[3px] ${theme.avatarBorder} shadow-lg overflow-hidden flex-shrink-0 mb-3 bg-white`}>
                          {pic ? (
                            <Img variant="generic" src={resolveImg(pic)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className={`w-full h-full bg-gradient-to-br ${getAvatarColor(member.id)} flex items-center justify-center text-white font-bold text-lg`}>
                              {displayName?.charAt(0).toUpperCase() || '?'}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Info */}
                      <h3 className="text-[15px] font-bold text-[#050505] group-hover:text-[#002249] transition-colors leading-tight truncate">
                        {isBusinessCard ? (cp.company_name || displayName) : displayName}
                        {isMe && <span className="text-[11px] text-[#65676B] font-normal"> (you)</span>}
                      </h3>
                      {member.leadership_level && (
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <LeadershipBadge badges={member.badges} leadershipLevel={member.leadership_level} leadershipTitle={member.leadership_title} />
                          <LeadershipTitle badges={member.badges} leadershipLevel={member.leadership_level} leadershipTitle={member.leadership_title} />
                        </div>
                      )}
                      {member.title && !member.leadership_level && <p className={`text-[12px] ${theme.titleColor} font-medium mt-0.5 truncate`}>{member.title}</p>}
                      {isBusinessCard && displayName && displayName !== cp.company_name && (
                        <p className="text-[12px] text-[#002249] font-medium mt-0.5 truncate">{displayName}</p>
                      )}
                      {cp?.company_name && !isBusinessCard && (
                        <p className="text-[13px] text-[#65676B] mt-1 flex items-center gap-1 truncate">
                          <Building2 className="w-3 h-3 flex-shrink-0" /> {cp.company_name}
                        </p>
                      )}
                      {cp?.industry && (
                        <p className="text-[11px] text-[#65676B] mt-0.5 flex items-center gap-1 truncate">
                          <Briefcase className="w-3 h-3 flex-shrink-0" /> {industryLabels[cp.industry] || cp.industry}
                        </p>
                      )}
                      {/* Stats */}
                      <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-100">
                        <span className="text-[11px] text-[#65676B]"><span className="font-semibold text-[#050505]">{member.post_count || 0}</span> posts</span>
                        {member.bio && <span className="text-[11px] text-[#65676B] truncate flex-1">{member.bio.slice(0, 40)}...</span>}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Users className="w-12 h-12 text-[#BCC0C4] mx-auto mb-3" />
            <p className="text-[15px] font-semibold text-[#050505]">No members found</p>
            <p className="text-sm text-[#65676B] mt-1">{search ? 'Try a different search term.' : 'Members will appear here once they join.'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalMembersDirectory;
